import type * as API from "@shared-core/data/api-types";

// Shared classifiers for the three strategy tiers. Used by both the
// detail-page strategy pyramid and the customers list "strategy" row
// card so labels and bands stay one source of truth. The SQL query
// that powers the list also mirrors these thresholds in CASE
// expressions (see ``modules/views_module/data_adapters/postgres_ro/
// customer_list_query.py``) — keep in lockstep.

export type BadgeVariant =
  "default" | "neutral" | "warning" | "error" | "success";

export type Tone = "red" | "amber" | "primary" | "green" | "muted";

// ────────────────────────────────────────────────────────────────
// Opportunity — untapped wallet size
// ────────────────────────────────────────────────────────────────

export function untappedTier(
  untappedGbp: number | null,
): { label: string; variant: BadgeVariant } | null {
  if (untappedGbp == null) return null;
  if (untappedGbp <= 0) return { label: "Tapped out", variant: "neutral" };
  if (untappedGbp < 10_000) return { label: "Token", variant: "neutral" };
  if (untappedGbp < 50_000) return { label: "Workable", variant: "default" };
  if (untappedGbp < 100_000) return { label: "Meaningful", variant: "default" };
  if (untappedGbp < 250_000) return { label: "Strategic", variant: "default" };
  return { label: "Whale", variant: "success" };
}

// Stable filter-pill keys (match the backend enum values).
export const UNTAPPED_TIER_KEYS = [
  "tapped_out",
  "token",
  "workable",
  "meaningful",
  "strategic",
  "whale",
  "unknown",
] as const;
export type UntappedTierKey = (typeof UNTAPPED_TIER_KEYS)[number];

export const UNTAPPED_TIER_LABEL: Record<UntappedTierKey, string> = {
  tapped_out: "Tapped out",
  token: "Token",
  workable: "Workable",
  meaningful: "Meaningful",
  strategic: "Strategic",
  whale: "Whale",
  unknown: "Unknown",
};

// ────────────────────────────────────────────────────────────────
// Relationship depth — contact coverage
// ────────────────────────────────────────────────────────────────

export function coverageBadge(
  coverage: API.CoverageLevel | null | undefined,
): { label: string; variant: BadgeVariant } | null {
  if (!coverage) return null;
  const map: Record<
    API.CoverageLevel,
    { label: string; variant: BadgeVariant }
  > = {
    absent: { label: "Unknown", variant: "neutral" },
    patchy: { label: "Patchy", variant: "warning" },
    adequate: { label: "Adequate", variant: "default" },
    good: { label: "Good", variant: "success" },
  };
  return map[coverage];
}

export const COVERAGE_TONE: Record<API.CoverageLevel, Tone> = {
  absent: "red",
  patchy: "amber",
  adequate: "primary",
  good: "green",
};

export const COVERAGE_KEYS = ["absent", "patchy", "adequate", "good"] as const;
export type CoverageKey = (typeof COVERAGE_KEYS)[number];

export const COVERAGE_LABEL: Record<CoverageKey, string> = {
  absent: "Unknown",
  patchy: "Patchy",
  adequate: "Adequate",
  good: "Good",
};

// ────────────────────────────────────────────────────────────────
// Account health — composite score
// ────────────────────────────────────────────────────────────────

export function healthScoreTier(
  score: number | null,
): { label: string; variant: BadgeVariant } | null {
  if (score == null) return null;
  if (score >= 80) return { label: "Thriving", variant: "success" };
  if (score >= 60) return { label: "Healthy", variant: "default" };
  if (score >= 40) return { label: "Watch", variant: "warning" };
  return { label: "At risk", variant: "error" };
}

/** Map a badge variant to its card-tone equivalent — shared by every
 * surface that derives a card ``tone`` from a tier's badge ``variant``. */
export function toneFromVariant(variant: BadgeVariant | undefined): Tone {
  if (variant === "success") return "green";
  if (variant === "warning") return "amber";
  if (variant === "error") return "red";
  return "primary";
}

export function healthTone(score: number | null): Tone {
  return toneFromVariant(healthScoreTier(score)?.variant);
}

export const HEALTH_TIER_KEYS = [
  "thriving",
  "healthy",
  "watch",
  "at_risk",
  "unknown",
] as const;
export type HealthTierKey = (typeof HEALTH_TIER_KEYS)[number];

export const HEALTH_TIER_LABEL: Record<HealthTierKey, string> = {
  thriving: "Thriving",
  healthy: "Healthy",
  watch: "Watch",
  at_risk: "At risk",
  unknown: "Unknown",
};

// Badge variant per tier — matches ``healthScoreTier``'s score-band mapping,
// keyed by tier instead of score so a rep-set override (which carries a
// tier, not a score) can render the same badge as the computed value.
export const HEALTH_TIER_VARIANT: Record<HealthTierKey, BadgeVariant> = {
  thriving: "success",
  healthy: "default",
  watch: "warning",
  at_risk: "error",
  unknown: "neutral",
};

// The four tiers a rep may set as a manual override. "unknown" is the
// computed side's null state and is never offered in the override picker.
export const OVERRIDABLE_HEALTH_TIER_KEYS = [
  "thriving",
  "healthy",
  "watch",
  "at_risk",
] as const;
export type OverridableHealthTierKey =
  (typeof OVERRIDABLE_HEALTH_TIER_KEYS)[number];

// ────────────────────────────────────────────────────────────────
// Account health factor weights — single source of truth for the
// composite score the frontend renders. The backend (overall-score
// signal, alert trigger, and ``customer_current_rollup.sql``)
// renormalises over exactly these scored factors, so the card hero and
// the value the backend sorted by stay aligned.
//
// Contact coverage (×20) and product breadth (×15) are deliberately
// absent: both come from enrichment, which holds no data in production,
// so they carried no information and were taken out of scoring. Their
// factor signals are still emitted by the backend but no surface scores
// them. Restore them here alongside the backend places — all together —
// once enrichment is populating. See
// ``docs/features/account-health-unscored-factors/01_notes.md``.
// ────────────────────────────────────────────────────────────────

export const ACCOUNT_HEALTH_FACTORS: ReadonlyArray<{
  label: string;
  signalName: string;
  weight: number;
}> = [
  {
    label: "Sales trend",
    signalName: "Account health: sales trend",
    weight: 30,
  },
  {
    label: "Buying recency",
    signalName: "Account health: buying recency",
    weight: 20,
  },
  { label: "Risk flags", signalName: "Account health: risk flags", weight: 15 },
];

// Sum of the scored factor weights — the divisor the weighted hero and
// each factor's contribution renormalise over, so the breakdown dialog's
// contributions reconcile with the hero number.
export const ACCOUNT_HEALTH_TOTAL_WEIGHT = ACCOUNT_HEALTH_FACTORS.reduce(
  (sum, f) => sum + f.weight,
  0,
);

export interface AccountHealthFactor {
  label: string;
  score: number | null;
  narrative: string | undefined;
  weight: number;
}

function parseSignalScore(signal: API.Signal | undefined): number | null {
  if (!signal) return null;
  const parsed = Number.parseInt(signal.signal_value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Resolve the scored factor rows from a list of account_health Signal records. */
export function resolveAccountHealthFactors(
  signals: API.Signal[],
): AccountHealthFactor[] {
  const byName = new Map(signals.map((s) => [s.signal_name, s]));
  return ACCOUNT_HEALTH_FACTORS.map((f) => {
    const sig = byName.get(f.signalName);
    return {
      label: f.label,
      score: parseSignalScore(sig),
      narrative: sig?.commentary ?? undefined,
      weight: f.weight,
    };
  });
}

/**
 * Read the composite score off the "Account health: overall score" signal.
 * Used by the customer-detail surfaces (pulse header, strategy card) where
 * the full account_health signal set is available on the customer object.
 * Returns null when the overall signal is absent or non-numeric.
 */
export function accountHealthScoreFromSignals(
  signals: API.Signal[] | null | undefined,
): number | null {
  const overall = (signals ?? []).find(
    (s) => s.signal_name === "Account health: overall score",
  );
  if (!overall) return null;
  const parsed = Number.parseInt(overall.signal_value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

/** Weighted composite score over non-null factor scores. */
export function computeHealthScore(
  factors: Array<{ score: number | null; weight: number }>,
): number | null {
  const scored = factors.filter(
    (f): f is { score: number; weight: number } => f.score !== null,
  );
  const totalWeight = scored.reduce((sum, f) => sum + f.weight, 0);
  if (totalWeight === 0) return null;
  return Math.round(
    scored.reduce((sum, f) => sum + (f.score * f.weight) / totalWeight, 0),
  );
}

/**
 * Recompute the composite score from the raw factor scores returned on
 * the customer list ``strategy_summary``. Matches the backend SQL so
 * the card hero and the value the backend sorted by agree.
 */
export function healthScoreFromFactorScores(
  scoresByName: Record<string, number | null> | undefined,
): number | null {
  if (!scoresByName) return null;
  return computeHealthScore(
    ACCOUNT_HEALTH_FACTORS.map((f) => ({
      score: scoresByName[f.signalName] ?? null,
      weight: f.weight,
    })),
  );
}
